/**
 * @module Repository Service
 * @param  {object} root    window/node global instance
 * @param  {function} factory Repository service factory
 * @return {RepositoryService} Repository Service instance
 */
(function(root, factory) {
  if (typeof define === 'function' && define.amd) {
    define('repositoryService', ['cookie-utils','es6-promise','localizerConfig'],
      function(cookieUtils, ES6PromisePolyfill, localizerConfig) {
        return factory(root, cookieUtils, ES6PromisePolyfill, localizerConfig);
      });
  } else {
    root.repositoryService = factory(root, window.cookieManager, ES6PromisePolyfill, localizerConfig);
  }
})(this, function(root, cookieUtils, ES6PromisePolyfill, localizerConfig) {

  var dataAccessStrategy = {};
  var Promise = Promise || ES6PromisePolyfill.Promise;

  /**
   * @function isExpired
   * @private
   * @description Checks if the value persisted in storage has expired or not.
   * @param  {object}  value value persisted in the storage.
   * @return {Boolean} true if the value is expired else false.
   */
  function isExpired(value) {
    var currentTime = new Date();
    return (value.createTime && value.expireTime !== undefined &&
      currentTime.getTime() - new Date(value.createTime).getTime() > value.expireTime);
  }

  /**
   * constant
   * @type {RegExp}
   */
  var urlPattern = /^http[s]{0,1}\:\/\/|^\/\/.+/; // http:// or https:// or //

  /**
   * @function isUrl
   * @private
   * @description Checks if the given key is URL or not.
   * @param  {string}  key key to be checked against URL pattern
   * @return {Boolean} true if the key is URL else false.
   */
  var isUrl = function(key) {
    return urlPattern.test(key);
  }

  /**
   * @private
   * @function cleanUp
   * @description Removes random function generated to receive callback and the script tags<br>
   * added in head tag.
   *
   * @param  {DomElement} script Script tag to be removed from the page.
   * @param  {function} generatedFunction random generated function to be removed from the window object.
   */
  var cleanUp = function(script, generatedFunction) {
    script.parentNode.removeChild(script);
    try {
      delete window[generatedFunction];
    } catch (e) {
      window[generatedFunction] = undefined;
    }
  }

  /**
   * @private
   * @function jsonp
   * @description Allows to invoke cross origin service for a given url.
   * @param  {string} url cross-origin service to be accessed.
   * @return {object} promise Promise, wrapping remote-service response
   */
  var jsonp = function(url) {
    return new Promise(function(resolve, reject) {
      url = url.indexOf('?') === -1 ? url + '?' : url + '&';
      var generatedFunction = 'loc' + Math.round(new Date().getTime() + Math.random() * 9999999);
      var script = document.createElement('script');
      script.src = url + 'callback=' + generatedFunction;
      document.getElementsByTagName("head")[0].appendChild(script);
      script.onerror = function(error) {
        cleanUp(script, generatedFunction);
        reject(error);
      }
      window[generatedFunction] = function(response) {
        cleanUp(script, generatedFunction);
        resolve(response);
      }
    });
  }

  /**
   * @private
   * @function isJson
   * @description Check if the given value is json or not.
   * @param  {object}  value value under test
   * @return {Boolean} true if value is json string else false.
   */
  var isJson = function(value) {
    if(typeof value === 'string') {
      try {
        JSON.parse(value)
      } catch(e) {
        return false;
      }
    }
    return true;
  }

 /**
   * @member
   * @description  Data Access Objects which read/writes to remote storage.
   * @type {Object}
   */
  dataAccessStrategy.httpDAO = {
    get: function(key) {
      return jsonp(key);
    },
    put: function(key, value, expireAfter, domain) {
      // to be implemented
    },
    remove: function(key) {
      // to be implemented
    }
  }

  cookieUtils.initializeCookieManager(window.THD_GLOBAL.cookieDomain);

  /**
   * @member
   * @description Data Access Objects which read/writes to cookie storage.
   * @type {Object}
   */
  dataAccessStrategy.cookieCrumbDAO = {

    get: function(key) {
      return cookieUtils.readCookie(key);
    },
    put: function(key, value, expireAfter, domain) {
      var expiryAfterDays = expireAfter/(24*60*60*1000);
      cookieUtils.createCookie(key, value, expiryAfterDays);
    },
    remove: function(key) {
      cookieUtils.cookieManager.removeCookie(key);
    }
  }

  /**
   * @member
   * @description Data Access Objects which read/writes to cookie storage.
   * @type {Object}
   */
  dataAccessStrategy.cookieDAO = {

    get: function(key) {
      return cookieUtils.readCookie(key);
    },
    put: function(key, value, expireAfter, domain) {
      if(expireAfter === 0) {
        cookieUtils.setSessionOnlyCookie(key, value, domain);
      } else {
        cookieUtils.setCookieExpireInSeconds(key, value, expireAfter/1000, domain);
      }
    },
    remove: function(key) {
      cookieUtils.cookieManager.removeCookie(key);
    }
  }

  /**
   * @member
   * @description Data Access Object which reads/writes to object on page.<br>
   * key/values stored using this strategy would exist only until next page refresh.
   * @type {Object}
   */
  dataAccessStrategy.pageDAO = {
    data : {},
    get: function(key) {
      return this.data[key];
    },
    put: function(key, value, expireTime, domain) {
      this.data[key] = value;
    },
    remove: function(key) {
      try {
        delete this.data[key];
      } catch(e) {
        this.data[key] = undefined;
      }
    }
  }


  /**
   * @member
   * @description Data Access Objects which read/writes to session storage
   * @type {Object}
   */
  dataAccessStrategy.sessionDAO = {
    get: function(key) {
      var value = sessionStorage.getItem(key);
      return isJson(value) ? JSON.parse(value) : value;
    },
    put: function(key, value, expireTime, domain) {
      if(typeof value === 'object') {
        sessionStorage.setItem(key, JSON.stringify(value), domain);
      } else {
        sessionStorage.setItem(key, value, domain);
      }
    },
    remove: function(key) {
      sessionStorage.removeItem(key);
    }
  }


  /**
   * @member
   * @description Data Access Objects which read/writes to local storage.
   * @type {Object}
   */
  dataAccessStrategy.localDAO = {

    get: function(key) {
      var value = localStorage.getItem(key);
      value = value && (isJson(value) && JSON.parse(value)) || value;
      if(value && isJson(value)) {
        return isExpired(value) ? localStorage.removeItem(key) : value.data;
      }
      return value;
    },

    put: function(key, value, expireTime, domain) {
      var wrapperValue = {
        'createTime': new Date()
      };
      wrapperValue.data = value;
      wrapperValue.expireTime = expireTime;
      localStorage.setItem(key, JSON.stringify(wrapperValue));
    },

    remove: function(key) {
      localStorage.removeItem(key);
    }
  }


  /**
   * @member
   * @description Storage strategies.
   * @type {Object}
   */
  var storage = {
    'cookieCrumb': dataAccessStrategy.cookieCrumbDAO,
    'cookie': dataAccessStrategy.cookieDAO,
    'http': dataAccessStrategy.httpDAO,
    'local': dataAccessStrategy.localDAO,
    'session': dataAccessStrategy.sessionDAO,
    'page': dataAccessStrategy.pageDAO
  }


   /**
   * @member
   * @description Key to Storage mapper. Maps a key to storage strategy.<br>
   * Any value to be store should be configured here.
   * @type {Object}
   */
  var workflowToStorageMapper = {
    storageConfig: localizerConfig.storageConfig,

    getDaoConfig: function(key, workflow) {
      return isUrl(key) ? this.storageConfig.get('HTTP_URL') :
         this.storageConfig.get(key, workflow);
    }
  }


  /**
   * @function getDao
   * @private
   * @description Returns the DAO instance. This can be Cookie, Local storage or Http.
   * @param  {String} key - Key whose value to be retrieved or written.
   * @param {string} storageStrategy - storage strategy key, optional, which allows consumer to choose the storage
   * @return {DataAccessObject}   Data Access Object.
   */
  var getDaoConfig = function(key, workflow) {
    return workflowToStorageMapper.getDaoConfig(key, workflow);
  };

  /**
   * @instance
   * @description Data Access Object Interface which chooses the storage strategy
   * based on the configuration.
   *
   * @type {object}
   */
  var dataAccessObject = {

    storageStrategy: storage,
    /**
     * @function get
     * @description Retrieves the value from the storage.
     * @param  {string} key key for the value to be retrieved.
     * @param {string} storageStrategy - storage strategy key, optional, which allows consumer to choose the storage
     * @return {object}   value for the given key.
     */
    get: function(key, workflow) {
      var daoConfig = getDaoConfig(key, workflow)
      var value = storage[daoConfig.storage].get(key);
      if(value === null || value === '' || value === 'undefined') {
        return undefined;
      }
      return value;
    },

    /**
     * @function put
     * @description Persists the value in the storage
     * @param  {string} key   key for the value to be persisted.
     * @param  {object} value  value to be persisted
     * @param  {long} expireAfter lifetime of the value.
     * ExpireAfter = 0  : Store only for a session
     * ExpireAfter = -1 : Store only for a page. Values do not exist between pages.
     * @param  {string} domain  domain under which value to be persisted.
     * @param {string} storageStrategy storage strategy key, optional, which allows consumer to choose the storage
     *
     */
    put: function(key, value, workflow) {
      var daoConfig = getDaoConfig(key, workflow);
      return storage[daoConfig.storage].put(key, value, daoConfig.expiry, window.THD_GLOBAL.cookieDomain);
    },

    /**
     * @function remove
     * @description Removes the value for the given key
     * @param  {string} key key for the value to be removed.
     * @param {string} storageStrategy - storage strategy key, optional, which allows consumer to choose the storage
     * @return {boolean} Returns true if the value successfully removed from storage else false.
     */
    remove: function(key, workflow) {
      var daoConfig = getDaoConfig(key, workflow);
      return storage[daoConfig.storage].remove(key);
    }
  }

  /**
   * Fix corrupted cookies
   */
  function fixLeadingZero() {
    var expireAfterDays = 1;

    try {
      var storeCrumb = cookieUtils.readCookie('THD_LOCSTORE');
      var storeId = storeCrumb.split('+')[0];

      if (storeId.substr(0, 1).indexOf('0') !== -1) {
        try {
          cookieUtils.readCookie('THD_PERSIST').split(':;').forEach(function(el, i){
            if (el.indexOf('C4_EXP') !== -1) {
              cookieUtils.createCookie('THD_LOCSTORE', storeCrumb.substr(1), 365);
              return;
            }
          })
        } catch (e) {}

      }
    } catch (e) {}
  }
  fixLeadingZero(); // Execute once

  /*
    ***************** Can be REMOVED in future ****************
    Pilot rollout of localizer persisted WORKFLOW in local storage.
    Since localstorage is not accessible across domains, this was a problem
    in browse and commerce pages. To mitigate cross domain access, WORKFLOW
    has been moved to cookie.
    Any page that had WORKFLOW in local storage will update the same to cookie.
    Any page that had no WORKFLOW will write to cookie.
  */
  function fixCrossDomainStorage() {
    var workflowCookie = dataAccessObject.get('WORKFLOW');

    if (!workflowCookie) {
      var workflow = dataAccessStrategy.localDAO.get('WORKFLOW');

      if (workflow) {
        dataAccessObject.put('WORKFLOW', workflow);
      }
    }
  };
  fixCrossDomainStorage();

  return dataAccessObject;

});



//////////////////
// WEBPACK FOOTER
// ./src/repositoryService.js
// module id = 0
// module chunks = 0